// Catalog.ID Client — Account Unlock Sheet Page
// ============================================================================
// The validator-sealed token as a document-set page (registration-restructure
// §5 page 3; personal accounts only — G1). Retitled "Account Unlock Sheet" in
// round 5 (2026-09-03) — it both unlocks a locked account and resets the
// password, and "unlock" is the verb the member is looking for. Redesigned from the standalone A5
// card: QR much bigger (cluttered-inkjet tolerance), checksum line dropped, no
// red text, nothing duplicating the Account header. The QR payload is the
// sealed envelope verbatim (binary — the plain-text-under-QR rule cannot apply).
//
// Named and worded for plan §12 (decided 2026-09-02): the sheet restores the
// LOGIN — it unlocks a locked account and can set a new password — and every
// use needs a code sent to the email or phone printed here, so the sheet alone
// gives nobody control. It opens no keys and no data; that is the supersecret's
// job. It survives its own use and survives a lock; only issuing a new sheet
// invalidates it.
//
// The software matches this text as of M9 (2026-09-02): the reset flow accepts
// locked accounts, unlocks them on completion, and treats the password change
// as optional.

import type { Content, TableCell } from 'pdfmake/interfaces';
import { qrToSvg } from '../qr-svg';
import { encodeAccessCard } from '../../crypto/reset-card';
import { accountHeader, type AccountHeaderData } from './shared';

export interface ResetCardPageData {
    header: AccountHeaderData;
    /** The validator-sealed token — rendered into the QR inside the CIDA
     *  framing, which adds the header's account id and nothing else. */
    sealedToken: Uint8Array;
    /** Where to start a reset, e.g. 'b4np.catalog.id'. Sheets are origin-bound. */
    nodeHost?: string;
    /** The verified channels a code can be sent to — the sheet's per-use second
     *  factor, so the holder can see which ones they will need. One header row,
     *  both values, exactly like the recovery sheet's. */
    contact?: { email?: string; phone?: string };
}

export function buildResetCardPage(data: ResetCardPageData): Content[] {
    const where = data.nodeHost ?? 'your Catalog.ID home node';

    const contactRows: TableCell[][] = [];
    const contact = [data.contact?.email, data.contact?.phone].filter((v): v is string => Boolean(v && v.trim()));
    if (contact.length > 0) {
        contactRows.push([
            { text: 'Contact', style: 'label' },
            { text: contact.join('   ·   '), style: 'value' },
        ]);
    }

    return [
        { text: 'Account Unlock Sheet', style: 'pageTitle' },
        accountHeader(data.header, contactRows),

        {
            svg: qrToSvg(encodeAccessCard({ cid: data.header.cid, sealedToken: data.sealedToken })),
            fit: [250, 250], alignment: 'center', margin: [0, 4, 0, 12],
        },

        {
            text: 'This sheet restores your login. It unlocks the account after it was locked, and can '
                + 'set a new password — after a lock raised on suspicion, or when the password is lost. '
                + 'Every use needs a code sent to the email or phone above, so the sheet alone gives '
                + 'nobody control. It cannot open keys or data; those need the supersecret.',
            style: 'value',
            margin: [0, 0, 0, 12],
        },

        { text: 'How to use it', style: 'section' },
        {
            ol: [
                `Open ${where} and choose “Unlock account / reset password”.`,
                'Scan or photograph this sheet when asked — it names your account.',
                'Choose email or phone — a code is sent there.',
                'Confirm the code. The account is unlocked; set a new password if you wish.',
            ],
            style: 'value',
            margin: [0, 0, 0, 12],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Handling', style: 'section' },
                {
                    ul: [
                        'Anyone holding this sheet AND the email or phone above can unlock the account '
                            + 'and change its password.',
                        'The sheet stays valid after use and after a lock. Issuing a new sheet makes this '
                            + 'one permanently invalid — do that if you suspect it was copied.',
                    ],
                    style: 'note',
                },
            ],
        },

    ];
}
